/**
 * Carteiras Modelo · Rebalanceador (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_rebalanceador.py).
 *
 * Ordens para levar a sleeve de ações/BDRs da carteira à carteira-modelo aderida, com
 * preços do BLP Relay. Posição do Fibery ou manual (upload, lido em memória). O basket
 * de ordens sai em Excel para envio manual à corretora. Na URL: carteira, aderência,
 * origem, override e ativos ignorados (o arquivo da posição manual não vai para a URL).
 */
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  FileInput,
  Loader,
  MultiSelect,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  ErroApi,
  apiGet,
  apiPostForm,
  apiPostFormBaixar,
  montarUrl,
  type Envelope,
} from '../../api/cliente';
import {
  BarraFiltros,
  CampoData,
  CampoOpcoes,
  GrupoFiltros,
  LARGURA,
} from '../../components/Filtros';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/carteiras-administradas/rebalanceador';

interface Carteira {
  carteira: string;
  modelos: { indice: number; rotulo: string; tipo: string }[];
}
interface Aviso {
  nivel: 'info' | 'warning' | 'error';
  texto: string;
}
interface Match {
  resumo: { matched: number; ambiguous: number; unmatched: number; total: number };
  tabela: TabelaApi;
}
export interface Dados {
  match?: Match | null;
  parada?: Aviso;
  cabecalho?: string;
  kpis?: KpiApi[];
  ignorar?: { valor: string; rotulo: string }[];
  ignorados?: string | null;
  avisos?: Aviso[];
  tabela?: TabelaApi;
  resumo?: KpiApi[];
  basket?: { tabela?: TabelaApi; custodiante?: string; aviso?: string; info?: string };
}

const COR: Record<Aviso['nivel'], string> = { info: 'blue', warning: 'yellow', error: 'red' };

function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

function Resultado({
  d,
  ignorar,
  aoIgnorar,
  baixar,
  baixando,
}: {
  d: Dados;
  ignorar: string[];
  aoIgnorar: (v: string[]) => void;
  baixar: React.ReactNode;
  baixando?: boolean;
}) {
  const r = d.match?.resumo;
  return (
    <>
      {d.match && r && (
        <Secao
          titulo="Qualidade do match"
          legenda={`${r.total} ativos: ${r.matched} com match, ${r.ambiguous} ambíguos, ${r.unmatched} sem match no cadastro do Fibery.`}
        >
          <TabelaDados tabela={d.match.tabela} altura={r.ambiguous + r.unmatched > 0 ? 300 : 200} />
        </Secao>
      )}
      {d.cabecalho && (
        <Text size="sm" c="dimmed">
          {d.cabecalho}
        </Text>
      )}
      {d.kpis && (
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          {d.kpis.map((k) => (
            <Kpi key={k.rotulo} kpi={k} />
          ))}
        </SimpleGrid>
      )}
      {d.ignorar && (
        <MultiSelect
          label="Ignorar ativos"
          description="Posições fora da sleeve: não geram ordem e o saldo não entra no alvo (papéis que não podem ser negociados)."
          data={d.ignorar.map((i) => ({ value: i.valor, label: i.rotulo }))}
          value={ignorar}
          onChange={aoIgnorar}
          searchable
          maw={720}
        />
      )}
      {d.ignorados && (
        <Text size="sm" c="dimmed">
          {d.ignorados}
        </Text>
      )}
      {d.parada && <Alert color={COR[d.parada.nivel]}>{d.parada.texto}</Alert>}
      {d.tabela && (
        <Secao titulo="Rebalanceamento">
          <TabelaDados tabela={d.tabela} alturaAutomatica />
        </Secao>
      )}
      {(d.avisos ?? []).map((a) => (
        <Alert key={a.texto} color={COR[a.nivel]}>
          {a.texto}
        </Alert>
      ))}
      {d.resumo && (
        <Secao titulo="Resumo das Operações">
          <SimpleGrid cols={{ base: 2, md: 5 }}>
            {d.resumo.map((k) => (
              <Kpi key={k.rotulo} kpi={k} />
            ))}
          </SimpleGrid>
        </Secao>
      )}
      {d.basket && (
        <Secao
          titulo="Basket de Ordens"
          legenda="Arquivo para envio manual à corretora; nada é enviado pelo dashboard."
          acoes={d.basket.tabela ? baixar : undefined}
        >
          {d.basket.aviso && <Alert color="yellow">{d.basket.aviso}</Alert>}
          {d.basket.info && <Alert color="blue">{d.basket.info}</Alert>}
          {d.basket.tabela && <TabelaDados tabela={d.basket.tabela} alturaAutomatica />}
          {baixando && <Loader size="sm" />}
        </Secao>
      )}
    </>
  );
}

export function Rebalanceador() {
  const [f, definir] = useFiltrosUrl({
    portfolio: '',
    modelo: 0,
    origem: 'fibery',
    override: 0,
    ignorar: '',
    relay: '',
  });
  const manual = f.origem === 'manual';
  const ignorar = f.ignorar ? f.ignorar.split(',') : [];
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [dataManual, setDataManual] = useState(() => new Date().toISOString().slice(0, 10));
  const [parcial, setParcial] = useState(false);

  const carteiras = useQuery({
    queryKey: ['carteiras.rebalanceador.carteiras'],
    queryFn: ({ signal }) =>
      apiGet<Envelope<{ carteiras: Carteira[] }>>(`${CAMINHO}/carteiras`, {}, signal),
    staleTime: 60 * 60 * 1000,
  });
  const escolhida = carteiras.data?.data.carteiras.find((c) => c.carteira === f.portfolio);
  const parametros = {
    portfolio: f.portfolio,
    modelo: f.modelo,
    override: f.override || undefined,
    ignorar,
    relay: f.relay || undefined,
  };
  const consulta = useQuery({
    queryKey: ['carteiras.rebalanceador', parametros],
    queryFn: ({ signal }) => apiGet<Envelope<Dados>>(CAMINHO, parametros, signal),
    enabled: !!f.portfolio && !manual,
    placeholderData: keepPreviousData,
  });

  const formulario = () => {
    const form = new FormData();
    form.append('arquivo', arquivo!);
    form.append('portfolio', f.portfolio);
    form.append('modelo', String(f.modelo));
    form.append('override', String(f.override));
    form.append('ignorar', ignorar.join(','));
    form.append('relay', f.relay);
    form.append('data', dataManual);
    form.append('parcial', String(parcial));
    return form;
  };
  // Manual: só calcula depois do clique em "Calcular"; daí em diante, mudar ignorados,
  // override ou preços recalcula com o mesmo arquivo (que nunca vai para a URL).
  const [enviado, setEnviado] = useState(false);
  const envio = useQuery({
    queryKey: [
      'carteiras.rebalanceador.manual',
      parametros,
      arquivo?.name,
      arquivo?.size,
      arquivo?.lastModified,
      dataManual,
      parcial,
    ],
    queryFn: () => apiPostForm<Envelope<Dados>>(`${CAMINHO}/manual`, formulario()),
    enabled: manual && enviado && !!arquivo && !!f.portfolio,
    placeholderData: keepPreviousData,
  });
  const download = useMutation({
    mutationFn: () => apiPostFormBaixar(`${CAMINHO}/manual/basket`, formulario()),
  });

  const d = manual ? (enviado ? envio.data?.data : undefined) : consulta.data?.data;
  const erro = manual ? envio.error : consulta.error;
  const carregando = manual ? envio.isFetching : consulta.isFetching;
  const custodiante = d?.basket?.custodiante;
  const baixar = manual ? (
    <Button
      size="xs"
      color="dourado"
      onClick={() => download.mutate()}
      loading={download.isPending}
    >
      Baixar basket · {custodiante} (Excel)
    </Button>
  ) : (
    <Button
      size="xs"
      color="dourado"
      component="a"
      href={montarUrl(`${CAMINHO}/basket`, parametros)}
    >
      Baixar basket · {custodiante} (Excel)
    </Button>
  );

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Carteiras Modelo · Rebalanceador"
        descricao="Ordens para levar a sleeve de ações e BDRs da carteira à carteira-modelo aderida, em lotes negociáveis, com preços do BLP Relay e basket em Excel por corretora."
        dadosAte={manual ? undefined : consulta.data?.meta.dados_ate}
        fonte="Fibery (clientes, carteiras-modelo, posições, ativos) · BLP Relay (Google Sheets, preços)"
      />
      <BarraFiltros carregando={carteiras.isFetching || carregando}>
        <GrupoFiltros titulo="Carteira">
          <Select
            label="Portfolio"
            placeholder="Selecione um portfolio..."
            data={(carteiras.data?.data.carteiras ?? []).map((c) => c.carteira)}
            value={f.portfolio || null}
            onChange={(portfolio) =>
              definir({ portfolio: portfolio ?? '', modelo: 0, ignorar: '', override: 0 })
            }
            searchable
            w={LARGURA.selecao - 60}
          />
          {escolhida && escolhida.modelos.length > 1 && (
            <Select
              label="Carteira-modelo"
              data={escolhida.modelos.map((m) => ({
                value: String(m.indice),
                label: m.rotulo,
              }))}
              value={String(f.modelo)}
              onChange={(v) => v && definir({ modelo: Number(v), ignorar: '' })}
              allowDeselect={false}
              w={LARGURA.selecao + 40}
            />
          )}
          <NumberInput
            label="Override Saldo Total"
            description="0 usa o saldo das posições"
            value={f.override}
            onChange={(v) => definir({ override: typeof v === 'number' ? v : 0 })}
            min={0}
            step={10000}
            decimalScale={2}
            thousandSeparator="."
            decimalSeparator=","
            w={LARGURA.numero + 50}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Posição">
          <CampoOpcoes
            rotulo="Origem da posição"
            valor={f.origem}
            aoMudar={(origem) => definir({ origem })}
            opcoes={[
              { valor: 'fibery', rotulo: 'Fibery' },
              { valor: 'manual', rotulo: 'Posição manual' },
            ]}
          />
          {manual && (
            <>
              <CampoData rotulo="Data da posição" valor={dataManual} aoMudar={setDataManual} />
              <FileInput
                label="Arquivo de posições"
                description="CSV ou Excel; Ativo e Quantidade obrigatórios"
                placeholder="Escolher arquivo"
                accept=".csv,.xlsx,.xls"
                value={arquivo}
                onChange={(a) => {
                  setArquivo(a);
                  setEnviado(false);
                }}
                clearable
                w={LARGURA.selecao}
              />
              <Stack gap={6} mt={22}>
                <Checkbox
                  label="Analisar apenas ativos com match"
                  checked={parcial}
                  onChange={(e) => setParcial(e.currentTarget.checked)}
                />
                <Anchor size="sm" href={montarUrl(`${CAMINHO}/template`)}>
                  Baixar template
                </Anchor>
              </Stack>
              <Button
                mt={25}
                color="dourado"
                disabled={!arquivo || !f.portfolio}
                onClick={() => (enviado ? void envio.refetch() : setEnviado(true))}
              >
                Calcular
              </Button>
            </>
          )}
        </GrupoFiltros>
        <GrupoFiltros titulo="Preços">
          <Button
            mt={25}
            variant="light"
            onClick={() => {
              definir({ relay: String(Date.now()) });
            }}
          >
            Atualizar BLP Relay
          </Button>
        </GrupoFiltros>
      </BarraFiltros>

      {!f.portfolio && <Alert color="blue">Selecione um portfolio.</Alert>}
      {f.portfolio && manual && !enviado && (
        <Alert color="blue">
          Envie um arquivo CSV/Excel com <b>Ativo</b> e <b>Quantidade</b> (use o template) e clique
          em Calcular. A posição manual substitui integralmente a do Fibery; o arquivo é lido em
          memória e não fica guardado.
        </Alert>
      )}
      {erro && (
        <Alert color="red">{mensagem(erro, 'Não foi possível calcular o rebalanceamento.')}</Alert>
      )}
      {download.isError && (
        <Alert color="red">{mensagem(download.error, 'Não foi possível gerar o basket.')}</Alert>
      )}
      {!manual && f.portfolio && consulta.isPending && <Loader />}
      {d && f.portfolio && (
        <Resultado
          d={d}
          ignorar={ignorar}
          aoIgnorar={(v) => {
            definir({ ignorar: v.join(',') });
          }}
          baixar={baixar}
        />
      )}
    </Pagina>
  );
}
